// Dictée vocale de la consigne (bouton micro) : capture audio (MediaRecorder) + transcription
// côté serveur. État d'enregistrement encapsulé ici -- le reste de l'app n'a besoin que de savoir
// "est-ce qu'un enregistrement est en cours ?" (isDictationRecording) et de pouvoir l'arrêter
// proprement au reset (stopDictationIfRecording), jamais de manipuler mediaRecorder directement.
import { autoResizeInstruction } from "./instructionInput";
import { mailData } from "../mailData";
import { PraetoriaTimeoutError, transcribeInstruction, TranscriptionError } from "../praetoria";
import { errorMessage } from "./domHelpers";

let mediaRecorder: MediaRecorder | null = null;
let recordedChunks: BlobPart[] = [];
let isRecording = false;
let isTranscribing = false;

export function isDictationRecording(): boolean {
  return isRecording;
}

// Utilisé par resetReplyPanel() (outlook.ts) pour couper un enregistrement en cours sans avoir à
// connaître mediaRecorder.
export function stopDictationIfRecording(): void {
  if (isRecording) {
    mediaRecorder?.stop();
  }
}

function setDictateStatus(message: string, isError = false): void {
  const dictateStatusElement = document.getElementById("dictate-status");
  dictateStatusElement.textContent = message;
  dictateStatusElement.className = isError ? "reply-status reply-status--error" : "reply-status";
}

function setDictateButtonState(state: "idle" | "recording" | "transcribing"): void {
  const dictateButton = document.getElementById("dictate-instruction");
  dictateButton.classList.toggle("is-recording", state === "recording");
  dictateButton.classList.toggle("is-disabled", state === "transcribing");
  dictateButton.setAttribute("aria-label", state === "recording" ? "Arrêter la dictée" : "Dicter la consigne");
  dictateButton.title = state === "recording" ? "Arrêter la dictée" : "Dicter la consigne";
}

async function runTranscription(audio: Blob): Promise<void> {
  const instructionElement = document.getElementById("reply-instruction") as HTMLTextAreaElement;
  isTranscribing = true;
  setDictateButtonState("transcribing");
  setDictateStatus("Transcription en cours...");

  try {
    if (!mailData.graphToken) {
      throw new Error("Session Microsoft expirée, rechargez le module.");
    }
    const text = await transcribeInstruction(audio, mailData.graphToken);
    instructionElement.value = instructionElement.value ? `${instructionElement.value} ${text}` : text;
    autoResizeInstruction();
    setDictateStatus("");
  } catch (error) {
    if (error instanceof PraetoriaTimeoutError) {
      setDictateStatus("Le service est actuellement en maintenance, merci de réessayer plus tard.", true);
    } else if (error instanceof TranscriptionError) {
      setDictateStatus(error.message, true);
    } else {
      setDictateStatus(`Impossible de transcrire la dictée (${errorMessage(error)}).`, true);
    }
  } finally {
    isTranscribing = false;
    setDictateButtonState("idle");
  }
}

function requestMicrophoneDevicePermission(): Promise<boolean> {
  return new Promise((resolve, reject) => {
    Office.devicePermission.requestPermissionsAsync([Office.DevicePermissionType.microphone], (asyncResult) => {
      if (asyncResult.status === Office.AsyncResultStatus.Failed) {
        reject(new Error("Permission d'accès au micro refusée."));
        return;
      }
      resolve(asyncResult.value);
    });
  });
}

async function startDictation(): Promise<void> {
  if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
    setDictateStatus("La dictée vocale n'est pas prise en charge dans ce client Outlook.", true);
    return;
  }
  if (!mailData.graphToken) {
    setDictateStatus("Session Microsoft expirée, rechargez le module.", true);
    return;
  }

  // Outlook (web et new Outlook Windows) bloque getUserMedia dans l'iframe du taskpane
  // tant que cette permission n'a pas été explicitement accordée via l'API Office.
  // isSetSupported("DevicePermissionService", "1.1") s'est révélé peu fiable pour ce set sur Outlook web
  // (rapporté à false alors que l'API est bien présente) — on détecte donc l'objet directement.
  const devicePermissionApiAvailable = typeof Office.devicePermission?.requestPermissionsAsync === "function";
  console.log("Office.devicePermission disponible :", devicePermissionApiAvailable);
  if (devicePermissionApiAvailable) {
    try {
      const isNewlyGranted = await requestMicrophoneDevicePermission();
      if (isNewlyGranted) {
        setDictateStatus("Permission micro accordée, redémarrage du module...");
        location.reload();
        return;
      }
    } catch (error) {
      setDictateStatus(errorMessage(error), true);
      return;
    }
  }

  let stream: MediaStream;
  try {
    stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  } catch (error) {
    console.error("getUserMedia a échoué :", error);
    setDictateStatus(`Permission d'accès au micro refusée (${errorMessage(error)}).`, true);
    return;
  }

  const mimeType = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
  mediaRecorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
  recordedChunks = [];

  mediaRecorder.ondataavailable = (event) => {
    if (event.data.size > 0) {
      recordedChunks.push(event.data);
    }
  };

  mediaRecorder.onstop = () => {
    stream.getTracks().forEach((track) => track.stop());
    isRecording = false;
    const audio = new Blob(recordedChunks, { type: mediaRecorder?.mimeType || "audio/webm" });
    void runTranscription(audio);
  };

  mediaRecorder.start();
  isRecording = true;
  setDictateButtonState("recording");
  setDictateStatus("Enregistrement en cours...");
}

export function handleDictateInstruction(): void {
  if (isTranscribing) {
    return;
  }
  if (isRecording) {
    mediaRecorder?.stop();
    return;
  }
  void startDictation();
}
